/**
 * PlatformTechDebtTab — Engineer-facing dashboard for the tech-debt watchlist.
 * iter84.29.
 *
 * Reads `/app/memory/tech_debt.md` (via GET /api/platform-admin/tech-debt) +
 * joins it with live LOC counts so the table is always honest about which
 * monolith files are over their target budget.
 *
 * Status colors mirror the backend:
 *   green   — current_loc <= target_max  (or target_max null)
 *   yellow  — target_max < current <= target_max * 1.5
 *   red     — current > target_max * 1.5
 *   missing — file/dir does not exist on disk
 *
 * Directories (path ending /) report the MAX file LOC in that directory —
 * post-split, you care whether any one file regrew, not the sum.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RefreshCw, AlertTriangle, CheckCircle2, CircleDashed, FileWarning, HandMetal, CalendarClock } from "lucide-react";


const STATUS_STYLES = {
    green: { dot: "bg-cyber", label: "OK", icon: CheckCircle2, text: "text-cyber" },
    yellow: { dot: "bg-blaze", label: "Over", icon: AlertTriangle, text: "text-blaze" },
    red: { dot: "bg-heat", label: "Critical", icon: AlertTriangle, text: "text-heat" },
    accepted: { dot: "bg-violet-500", label: "Accepted", icon: HandMetal, text: "text-violet-300" },
    missing: { dot: "bg-zinc-700", label: "Missing", icon: FileWarning, text: "text-zinc-500" },
};

function StatusPill({ status }) {
    const s = STATUS_STYLES[status] || STATUS_STYLES.missing;
    const Icon = s.icon;
    return (
        <span data-testid={`tech-debt-status-${status}`} className={`pill text-[10px] ${s.text} border-current/40 bg-current/10 inline-flex items-center gap-1`}>
            <Icon className="w-3 h-3" /> {s.label}
        </span>
    );
}


export default function PlatformTechDebtTab() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await api.getPlatformTechDebt();
            setData(d);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load tech-debt watchlist");
        }
        setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return <p className="text-xs text-zinc-500 italic">Loading watchlist…</p>;
    }
    if (!data) return null;

    const { rows, summary, generated_at } = data;
    const overBudgetCount = (summary.yellow || 0) + (summary.red || 0);

    return (
        <div className="space-y-4" data-testid="platform-tech-debt-tab">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-extrabold text-2xl uppercase tracking-tight">Tech Debt Watchlist</h2>
                    <p className="text-xs text-zinc-400 mt-1">
                        Monolith files we're tracking against a per-file LOC budget. Rows update
                        live from disk; the durable record is <code className="text-cyber">/app/memory/tech_debt.md</code>.
                    </p>
                </div>
                <button
                    data-testid="tech-debt-refresh-btn"
                    onClick={load}
                    disabled={loading}
                    className="brutal-btn-ghost text-xs py-1.5 disabled:opacity-50"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            {/* Summary pills */}
            <div className="flex flex-wrap gap-2" data-testid="tech-debt-summary">
                <span className="pill bg-cyber/10 border-cyber/40 text-cyber text-[11px]">
                    <CheckCircle2 className="w-3 h-3" /> {summary.green} OK
                </span>
                <span className="pill bg-blaze/10 border-blaze/40 text-blaze text-[11px]">
                    <AlertTriangle className="w-3 h-3" /> {summary.yellow} over budget
                </span>
                <span className="pill bg-heat/10 border-heat/40 text-heat text-[11px]">
                    <AlertTriangle className="w-3 h-3" /> {summary.red} critical
                </span>
                {(summary.accepted || 0) > 0 && (
                    <span className="pill bg-violet-500/10 border-violet-500/40 text-violet-300 text-[11px]" data-testid="tech-debt-accepted-pill">
                        <HandMetal className="w-3 h-3" /> {summary.accepted} accepted
                    </span>
                )}
                {(summary.planned || 0) > 0 && (
                    <span className="pill bg-sky-500/10 border-sky-500/40 text-sky-300 text-[11px]" data-testid="tech-debt-planned-pill">
                        <CalendarClock className="w-3 h-3" /> {summary.planned} planned
                    </span>
                )}
                {summary.missing > 0 && (
                    <span className="pill bg-zinc-700/20 border-zinc-700 text-zinc-500 text-[11px]">
                        <CircleDashed className="w-3 h-3" /> {summary.missing} not on disk
                    </span>
                )}
                <span className="text-[10px] text-zinc-500 self-center ml-1">
                    Updated {new Date(generated_at).toLocaleTimeString()}
                </span>
            </div>

            {overBudgetCount > 0 && (
                <div className="brutal-card-light p-3 text-xs text-zinc-300 border-blaze/40" data-testid="tech-debt-callout">
                    <span className="font-bold text-blaze">{overBudgetCount}</span> file{overBudgetCount === 1 ? "" : "s"} above
                    target. The biggest wins come from the rows marked critical — splitting one of those typically
                    knocks 800+ lines off the worst-offender list.
                </div>
            )}

            {/* Table */}
            <div className="brutal-card p-0 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-ink">
                            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#222]">
                                <th className="py-2 pl-3 pr-2">Path</th>
                                <th className="py-2 px-2 text-right">Current</th>
                                <th className="py-2 px-2 text-right">Target</th>
                                <th className="py-2 px-2">Status</th>
                                <th className="py-2 px-2">Last sweep</th>
                                <th className="py-2 px-2">Note</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const overBy = (r.current_loc != null && r.target_max)
                                    ? r.current_loc - r.target_max
                                    : null;
                                return (
                                    <tr key={r.path} data-testid={`tech-debt-row-${r.path}`} className="border-b border-[#181818] last:border-b-0">
                                        <td className="py-2 pl-3 pr-2 font-mono text-white text-[11px]">{r.path}</td>
                                        <td className={`py-2 px-2 text-right font-mono ${r.status === "red" ? "text-heat font-bold" : r.status === "yellow" ? "text-blaze" : "text-zinc-300"}`}>
                                            {r.current_loc != null ? r.current_loc : "—"}
                                            {overBy != null && overBy > 0 && (
                                                <span className="text-zinc-500 text-[9px] ml-1">(+{overBy})</span>
                                            )}
                                        </td>
                                        <td className="py-2 px-2 text-right font-mono text-zinc-400">
                                            {r.target_max || "—"}
                                        </td>
                                        <td className="py-2 px-2">
                                            <StatusPill status={r.status} />
                                            {r.status === "accepted" && r.underlying_status !== "green" && (
                                                <span className="text-[9px] text-zinc-600 ml-1" title="Computed LOC status — kept honest even though the debt is accepted">
                                                    (was {r.underlying_status})
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2 px-2 text-zinc-400 text-[10px] whitespace-nowrap">
                                            {r.last_iter ? (
                                                <>
                                                    <span className="text-cyber">{r.last_iter}</span>
                                                    {r.last_date && <span className="text-zinc-500"> · {r.last_date}</span>}
                                                </>
                                            ) : (
                                                <span className="text-zinc-600 italic">never</span>
                                            )}
                                        </td>
                                        <td className="py-2 px-2 text-zinc-400 text-[10px]">{r.note || "—"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="text-[10px] text-zinc-500 italic px-1">
                Add a new row to <code className="text-cyber">/app/memory/tech_debt.md</code> any time a file
                crosses ~800 lines. Disposition decisions (<span className="text-sky-300">plan to resolve</span> ·{" "}
                <span className="text-violet-300">accept issue</span>) are made in{" "}
                <span className="text-cyber font-bold">Stewardship → Tech Debt</span> — this table is the
                metrics/detail view and re-reads the markdown on every refresh.
            </p>
        </div>
    );
}
